import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "../../utils/cn";
import { Field } from "../base/Field";
import { parseIndianNumber, formatINRNumber } from "../../utils/format";

interface CurrencyInputProps {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  value?: number | null;
  onChange?: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
}

/**
 * The single money input in the app. Accepts only numeric entry, shows a rupee
 * prefix, and reformats with Indian grouping when the field loses focus.
 */
export function CurrencyInput({
  label,
  helper,
  error,
  required,
  value,
  onChange,
  placeholder = "0.00",
  disabled,
  name,
}: CurrencyInputProps) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const suppressRef = useRef(false);

  useEffect(() => {
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }
    if (focused) return;
    if (value === null || value === undefined || Number.isNaN(value)) {
      setText("");
    } else {
      setText(formatINRNumber(value));
    }
  }, [value, focused]);

  const handleChange = (raw: string) => {
    // Numeric only: digits and a single decimal point.
    let next = raw.replace(/[^\d.]/g, "");
    const firstDot = next.indexOf(".");
    if (firstDot !== -1) {
      next = next.slice(0, firstDot + 1) + next.slice(firstDot + 1).replace(/\./g, "");
    }
    if (next.split(".")[1]?.length > 2) next = next.slice(0, next.indexOf(".") + 3);
    setText(next);
    suppressRef.current = true;
    const parsed = parseIndianNumber(next);
    onChange?.(Number.isNaN(parsed) ? null : parsed);
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseIndianNumber(text);
    if (Number.isNaN(parsed)) {
      setText("");
      onChange?.(null);
    } else {
      setText(formatINRNumber(parsed));
      onChange?.(parsed);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // ArrowUp/Down nudge the amount by 1.
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const current = parseIndianNumber(text);
      const base = Number.isNaN(current) ? 0 : current;
      const next = e.key === "ArrowUp" ? base + 1 : base - 1;
      setText(String(next));
      onChange?.(next);
    }
  };

  return (
    <Field label={label} helper={helper} error={error} required={required}>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-8 items-center justify-center text-sm text-foreground-500">
          ₹
        </span>
        <input
          type="text"
          inputMode="decimal"
          name={name}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            "tnum h-9 w-full rounded-md border bg-background-50 pl-8 pr-3 text-left text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500",
            error ? "border-danger-300 focus:ring-danger-300/30" : "border-background-300"
          )}
          aria-invalid={Boolean(error)}
        />
      </div>
    </Field>
  );
}